import React, { useState, type FormEvent } from 'react';
import { NumberInput, RadioButtonGroupInput } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { updateChargeRateCents, updateProRateCents } from '../PositionRateSettingsForm.utils';

type Props = {
  source: string;
  inheritedSettings: any;
};

const choices = [
  { id: 'lock_charge_rate', name: 'Lock Charge Rate' },
  { id: 'lock_professional_rate', name: 'Lock Professional Rate' },
];

export const BaseMarginPercentW2LocalInput = ({ source, inheritedSettings }: Props) => {
  const [isLockOptionVisible, setIsLockOptionVisible] = useState(false);
  const [lockedRateType, setLockedRateType] = useState<
    'lock_professional_rate' | 'lock_charge_rate' | null
  >(null);

  const { watch, setValue } = useFormContext();
  const rateCents = watch(`${source}.charge_rate_cents`);
  const baseMarginPercent = watch(`${source}.base_margin_percent_w2`);
  const professionalRateCents = watch(`${source}.pro_rate_cents`);
  const markUp = watch(`${source}.blended_rates.blended_rate_markup`);
  const useBlendedRate = watch(`${source}.use_blended_rate`);

  let effectiveChargeRate = rateCents;
  if (useBlendedRate && markUp) {
    effectiveChargeRate = rateCents * (1 + markUp);
  }

  const handleChange = (value: number) => {
    if (lockedRateType === 'lock_professional_rate') {
      updateChargeRateCents({
        setValue,
        watch,
        source,
        baseMarginPercent: value,
        inheritedSettings,
        professionalRateCents,
      });
    } else {
      updateProRateCents({
        watch,
        source,
        setValue,
        baseMarginPercent: value,
        inheritedSettings,
        rateCents: effectiveChargeRate,
      });
    }
  };

  function handleLockOptionChange(event: FormEvent<HTMLDivElement>) {
    const value = (event.target as HTMLInputElement).value as
      | 'lock_professional_rate'
      | 'lock_charge_rate';
    setLockedRateType(value);
    setValue(`${source}.lockedRateType`, value);

    if (value === 'lock_professional_rate') {
      updateChargeRateCents({
        setValue,
        watch,
        source,
        baseMarginPercent,
        inheritedSettings,
        professionalRateCents,
      });
    } else {
      updateProRateCents({
        setValue,
        watch,
        source,
        baseMarginPercent,
        inheritedSettings,
        rateCents: effectiveChargeRate,
      });
    }
  }

  return (
    <>
      <NumberInput
        source={`${source}.base_margin_percent_w2`}
        label="Base Margin Percent W2"
        onChange={(event) => {
          const newValue = parseFloat(event.target.value);
          if (!isNaN(newValue)) {
            handleChange(newValue);
          }
        }}
        onFocus={() => setIsLockOptionVisible(true)}
        helperText={String(inheritedSettings?.['base_margin_percent_w2'] || '')}
      />
      {isLockOptionVisible && (
        <RadioButtonGroupInput
          source="lock_rate"
          choices={choices}
          defaultValue="lock_charge_rate"
          label="Calculation Base"
          helperText="Select which rate will be fixed. The other will be automatically calculated based on the entered margin."
          onChange={handleLockOptionChange}
        />
      )}
    </>
  );
};
